/**
 * UserMultiPicker — several people, one field (ASSIGN1).
 *
 * The multi-select sibling of ``UserPicker``, and a wrapper in exactly
 * the same sense: it owns nothing about searching or paging, only the
 * three things that are about PEOPLE — the label, the second line, and
 * which ids may not be offered. Search, pagination, chips, hydration and
 * keyboard handling all come from ``AsyncMultiCombobox``, and the option
 * shape comes from the same PICK1c routes ``UserPicker`` uses.
 *
 * Nothing here re-decides who may be picked. That rule lives in one
 * place on the server (``user_picker_service``): active users of the
 * caller's organisation. A second definition on the client would drift
 * from the one the API enforces.
 *
 * ``excludeIds`` is how the primary assignee stays out of the list. It
 * filters the fetched page rather than the selection, because the point
 * is to make the double-role unpickable, not to silently drop a chip the
 * user already has — the server removes that one canonically, and the
 * form mirrors it (TaskFormModal). ``total`` is reduced by whatever the
 * page dropped so "Mehr laden" does not offer a page that is already
 * exhausted.
 */
import React from "react";

import {
  fetchAdminUserPickerOptionById,
  fetchAdminUserPickerOptions,
} from "../../../api/adminData";
import AsyncMultiCombobox from "../../../components/ui/AsyncMultiCombobox";
import { userName, userSecondaryLine } from "./UserPicker";

const PAGE_SIZE = 20;

export default function UserMultiPicker({
  value = [],
  onChange,
  selectedItems = [],
  excludeIds = [],
  label = "Weitere Beteiligte",
  placeholder = "— keine —",
  searchPlaceholder = "Person nach Name oder E-Mail suchen …",
  disabled = false,
  footer = null,
  /**
   * REPORT-PEOPLE: optional ceiling on how many people may be selected.
   * Unset means unlimited, which is what ASSIGN1's task form has always
   * been and stays — this prop is additive and changes nothing for a
   * caller that does not pass it.
   *
   * Enforced by refusing further ADDITIONS, never by trimming the
   * selection: silently dropping a chip someone already picked would
   * hide the limit instead of stating it. Removals are always allowed,
   * so a selection that arrived over the cap can still be fixed.
   */
  maxSelections = null,
}) {
  // String-normalised so a numeric id and its string form cannot slip
  // through as two different people (the same normalisation the calendar
  // filters use).
  const excluded = React.useMemo(
    () => new Set((excludeIds || []).filter(Boolean).map(String)),
    [excludeIds]
  );

  const cap =
    typeof maxSelections === "number" && maxSelections > 0
      ? maxSelections
      : null;
  const selectedCount = Array.isArray(value) ? value.length : 0;
  const atCap = cap !== null && selectedCount >= cap;

  const handleChange = React.useCallback(
    (next) => {
      const list = Array.isArray(next) ? next : [];
      // Growing past the cap is the only rejected transition.
      if (cap !== null && list.length > cap && list.length > selectedCount) {
        return;
      }
      onChange(list);
    },
    [cap, onChange, selectedCount]
  );

  const fetchPage = React.useCallback(
    async ({ query, offset, limit }) => {
      const data = await fetchAdminUserPickerOptions({
        q: query || undefined,
        limit,
        offset,
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      if (excluded.size === 0) return data;
      const kept = items.filter((u) => !excluded.has(String(u.id)));
      return {
        ...data,
        items: kept,
        total: Math.max(0, Number(data?.total || 0) - (items.length - kept.length)),
      };
    },
    [excluded]
  );

  // Only fires for an id that no `selectedItems` entry could name — in
  // practice never on the task form, whose detail response ships the
  // participants with their labels. Kept because a caller that prefills
  // ids without labels must still show names rather than placeholders.
  const hydrateById = React.useCallback(
    (id) => fetchAdminUserPickerOptionById(id),
    []
  );

  return (
    <AsyncMultiCombobox
      label={label}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      loadingText="Personen werden geladen …"
      emptyText="Keine passenden Personen gefunden."
      errorText="Personen konnten nicht geladen werden."
      value={value}
      selectedItems={selectedItems}
      onChange={handleChange}
      fetchPage={fetchPage}
      getItemId={(u) => u.id}
      getItemLabel={userName}
      hydrateById={hydrateById}
      pageSize={PAGE_SIZE}
      disabled={disabled}
      footer={
        atCap ? (
          <p className="m-0 text-[11px] text-muted-foreground">
            Maximal {cap} Personen ausgewählt.
          </p>
        ) : (
          footer
        )
      }
      renderItem={(user) => (
        <span className="block min-w-0">
          <span
            title={userName(user)}
            className={
              "block truncate text-[13px] " +
              (user.is_active === false
                ? "font-normal text-muted-foreground"
                : "font-medium text-foreground")
            }
          >
            {userName(user)}
          </span>
          <span
            title={userSecondaryLine(user)}
            className="block truncate text-[11px] text-muted-foreground"
          >
            {userSecondaryLine(user)}
          </span>
        </span>
      )}
    />
  );
}
